// @ts-strict-ignore
import React from 'react'
import Link from 'components/Link'
import { Button, Card, CardActions, CardContent, CardHeader, Typography } from '@mui/material'
import Condition from 'components/Condition'
import StyledLink from 'components/StyledLink'
import UserAvatar from 'components/UserAvatar'
import { useFragment } from 'react-relay/hooks'
import { graphql } from 'relay-runtime'
import { BlogPostItem_BlogPost$key } from '__generated__/BlogPostItem_BlogPost.graphql'
import { formatAsDate } from 'services/formatting'
import useStyles from './styles'

export interface IBlogPostItemProps {
  blogPostFragmentRef: BlogPostItem_BlogPost$key | null
  action?: React.ReactNode
  variant?: 'elevation' | 'outlined'
  raised?: boolean
  status?: string
}

function BlogPostItem(props: IBlogPostItemProps) {
  const { blogPostFragmentRef, action, variant, status, raised } = props
  const { classes } = useStyles()
  const blogPost = useFragment(graphql`
    fragment BlogPostItem_BlogPost on BlogPost {
      title
      excerpt
      slug
      blogCategories {
        name
        slug
      }
      createdAt
      estimatedReadingTimeMinutes
      viewsCount
      user {
        name
        profile {
          slug
        }
        ...UserAvatarContainer_User
      }
    }
  `, blogPostFragmentRef)

  if (!blogPost) {
    return null
  }

  const estimatedReadingTimeMinutes = blogPost?.estimatedReadingTimeMinutes || 0
  const isDraft = status === 'draft'
  const isWaitingApproval = status === 'published'
  const isApproved = status === 'approved'
  const userSlug = blogPost.user.profile.slug
  const postSlug = blogPost.slug

  return (
    <Card className={classes.container} variant={variant} raised={raised}>
      <CardHeader
        avatar={(<UserAvatar userFragmentRef={blogPost.user} />)}
        title={(
          <Typography variant="h4">
            <StyledLink href={`/blog/${userSlug}/${postSlug}`} style={{ textDecoration: 'none' }}>
              {blogPost.title}
            </StyledLink>
          </Typography>
        )}
        subheader={(
          <React.Fragment>
            By <StyledLink href={`/${userSlug}`} style={{ textDecoration: 'none' }}>{blogPost.user?.name}</StyledLink>
            {/* Safe access to blogCategories: only render category if array exists and has items */}
            {blogPost.blogCategories?.[0] && (
              <React.Fragment> in {blogPost.blogCategories[0].name}</React.Fragment>
            )}
            <div>{formatAsDate(blogPost.createdAt)}</div>
          </React.Fragment>
        )}
        action={action}
      />

      <CardContent>
        <Typography variant="body1">
          {blogPost?.excerpt}
        </Typography>
      </CardContent>

      <CardActions className={classes.actions}>
        <Typography variant="subtitle1" style={{ marginRight: 'auto' }}>
          {estimatedReadingTimeMinutes < 1 ? '<1' : estimatedReadingTimeMinutes} minute read
        </Typography>

        <Condition condition={isWaitingApproval}>
          <Typography variant="subtitle2" style={{ marginRight: 4 }}>
            Waiting approval
          </Typography>
        </Condition>

        <Condition condition={isApproved}>
          <Typography variant="subtitle2" style={{ marginRight: 4 }}>
            {blogPost.viewsCount} {blogPost.viewsCount > 1 ? 'views' : 'view'}
          </Typography>
        </Condition>

        <Link href={`/blog/${userSlug}/${postSlug}${isDraft ? '/edit' : ''}`}>
          <Button color="secondary">
            {isDraft ? 'Finish Writing' : 'Read More'}
          </Button>
        </Link>

        <Condition condition={isDraft}>
          <Link href={`/blog/${userSlug}/${postSlug}`}>
            <Button color="primary">
              Preview
            </Button>
          </Link>
        </Condition>
      </CardActions>
    </Card>
  )
}

export default React.memo(BlogPostItem)
